import React, { useContext, useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import type { NextRouter } from 'next/router'
import { useRouter } from 'next/router'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import { Icon } from '@iconify/react'
import type { AnyAbility } from '@casl/ability'
import type { NavGroup, NavLink, VerticalNavItemsType } from 'src/@core/layouts/types'
import type { UserDataType } from 'src/context/types'
import { AbilityContext } from 'src/layouts/components/acl/Can'
import { useAuth } from '@/src/hooks/useAuth'
import { useGlobalState } from '@/src/pages/_app'
import { resolveNavPath } from 'src/@core/layouts/utils'
import { filterSubNavTreeByAcl, isNavGroup, isNavSectionTitle } from './navGuards'
import { isNavGroupSubtreeActive, isPrimaryNavLinkActive } from './navLinkActive'
import { bhyveNavTokens as T } from './bhyveNavTokens'

function canShowNavItem(item: NavLink, ability: AnyAbility | undefined, user: UserDataType | null): boolean {
  if (item.auth === false) return true
  if (item.customPermissionCheck && user) return item.customPermissionCheck(user)

  return Boolean(ability && ability.can(item?.action, item?.subject))
}

const pillSx = (active: boolean) => ({
  borderRadius: 999,
  textTransform: 'none' as const,
  fontWeight: active ? 700 : 500,
  fontSize: 14,
  px: 2,
  py: 0.75,
  minHeight: 36,
  flexShrink: 0,
  whiteSpace: 'nowrap' as const,
  color: active ? T.pillActiveText : T.pillInactiveText,
  bgcolor: active ? T.pillActiveBg : 'transparent',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: active ? T.pillActiveBg : 'rgba(23, 23, 23, 0.06)'
  }
})

const subNavDropdownItemSx = (active: boolean) => ({
  typography: 'body2' as const,
  fontWeight: active ? 700 : 500,
  color: T.sidebarText,
  borderLeft: `3px solid ${active ? T.brandYellow : 'transparent'}`,
  bgcolor: active ? T.sidebarRowActiveBg : 'transparent',
  '&:hover': {
    bgcolor: active ? T.sidebarRowActiveHoverBg : 'rgba(23, 23, 23, 0.06)'
  }
})

function findDeepestActiveSecondaryLabel(
  nodes: VerticalNavItemsType,
  router: NextRouter,
  id: string | string[] | undefined
): string | null {
  for (const node of nodes) {
    if (isNavSectionTitle(node)) continue

    if (isNavGroup(node)) {
      const children = (node.children || []) as VerticalNavItemsType
      const fromChild = findDeepestActiveSecondaryLabel(children, router, id)
      if (fromChild) return fromChild
      if (isNavGroupSubtreeActive(router, node, id)) return node.title
    } else {
      const link = node as NavLink
      const href = resolveNavPath(link.path, id)
      if (href && isPrimaryNavLinkActive(router, href)) return link.title
    }
  }

  return null
}

export const BHYVE_SUBNAV_MIN_HEIGHT_PX = 52

export interface BhyveHorizontalSubnavV2Props {
  subNavItems: VerticalNavItemsType
  subNavItemsMobile: VerticalNavItemsType
  subNavigationRestaurant: VerticalNavItemsType
  productSubNavItems: VerticalNavItemsType
  franchiseSubNavItems: VerticalNavItemsType

  /** When the main content area is scrolled, strengthens blur/shadow (Figma: blur on scroll). */
  elevated?: boolean
}

export const BhyveHorizontalSubnavV2: React.FC<BhyveHorizontalSubnavV2Props> = ({
  subNavItems,
  subNavigationRestaurant,
  productSubNavItems,
  franchiseSubNavItems,
  elevated = false
}) => {
  const theme = useTheme()
  const router = useRouter()
  const { id } = router.query
  const { user } = useAuth()
  const ability = useContext(AbilityContext)
  const { isChildPage, isProductPage, isFranchisePage } = useGlobalState()

  /** Bottom sheet on phones only; tablets (`sm`–`lg`) use horizontally scrollable pills. */
  const useSecondaryNavSheet = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true })

  /** Pages under `/app/restaurant/[id]/…` must show this bar even when `Layout` is not used (e.g. kitchen-management OMS pages). */
  const isRestaurantScopedRoute =
    router.isReady &&
    typeof id === 'string' &&
    (router.pathname === '/app/restaurant/[id]' || router.pathname.startsWith('/app/restaurant/[id]/'))

  /** Franchise detail + review workflows — legacy shell had no horizontal subnav on these routes. */
  const hideFranchiseHorizontalSubnav =
    router.isReady &&
    [
      '/app/franchise-hub/[id]',
      '/app/franchise-hub/add/[reviewId]',
      '/app/franchise-hub/review-signoff/[reviewId]',
      '/app/franchise-hub/review-submission/[reviewId]'
    ].includes(router.pathname)
  const showRestaurantHorizontalSubnav = isChildPage || isRestaurantScopedRoute

  const sourceTree = useMemo((): VerticalNavItemsType | null => {
    if (isFranchisePage && hideFranchiseHorizontalSubnav) return null
    if (isFranchisePage) return franchiseSubNavItems
    if (isProductPage) return productSubNavItems
    if (showRestaurantHorizontalSubnav) {
      if (user?.role.name === 'Restaurant Team') return subNavigationRestaurant

      /** Always full desktop tree — sheet fits all sections (Kitchen Management, Reporting, …). Kept prop for legacy shell API. */
      return subNavItems
    }

    return null
  }, [
    franchiseSubNavItems,
    hideFranchiseHorizontalSubnav,
    showRestaurantHorizontalSubnav,
    isFranchisePage,
    isProductPage,
    productSubNavItems,
    subNavItems,
    subNavigationRestaurant,
    user?.role.name
  ])

  const filteredTree = useMemo(() => {
    if (!sourceTree) {
      return []
    }

    return filterSubNavTreeByAcl(sourceTree, item => canShowNavItem(item, ability, user))
  }, [sourceTree, ability, user])

  const [sheetOpen, setSheetOpen] = useState(false)

  const compactNavLabel = useMemo(
    () => findDeepestActiveSecondaryLabel(filteredTree, router, id) ?? 'Sections',
    [filteredTree, router, id]
  )

  useEffect(() => {
    const close = () => setSheetOpen(false)
    router.events.on('routeChangeComplete', close)

    return () => {
      router.events.off('routeChangeComplete', close)
    }
  }, [router.events])

  if (!filteredTree.length) return null

  return (
    <Box
      data-bhyve-secondary-nav
      sx={{
        position: 'relative',
        borderBottom: `1px solid ${T.secondaryBarBorder}`,
        backgroundColor: T.secondaryBarBg,
        backdropFilter: elevated ? 'blur(14px) saturate(1.2)' : 'blur(8px)',
        boxShadow: elevated ? '0 6px 20px rgba(23, 23, 23, 0.08)' : 'none',
        transition: 'box-shadow 0.2s ease, backdrop-filter 0.2s ease',
        px: 2,
        py: 1,
        flexShrink: 0,
        minWidth: 0
      }}
    >
      {useSecondaryNavSheet ? (
        <>
          <Box
            data-bhyve-secondary-nav-compact
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minHeight: BHYVE_SUBNAV_MIN_HEIGHT_PX,
              minWidth: 0
            }}
          >
            <IconButton
              color='inherit'
              aria-label='Open sections navigation'
              aria-expanded={sheetOpen ? 'true' : 'false'}
              aria-haspopup='dialog'
              onClick={() => setSheetOpen(true)}
              size='medium'
              sx={{ color: T.sidebarText, flexShrink: 0 }}
            >
              {/* Distinct from primary rail hamburger (`tabler:menu-2`) — reads as section layout */}
              <Icon icon='tabler:layout-grid' width={24} height={24} aria-hidden />
            </IconButton>
            <Typography
              variant='body2'
              component='span'
              noWrap
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: 600,
                fontSize: 14,
                color: T.sidebarText
              }}
            >
              {compactNavLabel}
            </Typography>
          </Box>
          <SecondaryNavSectionsSheet
            open={sheetOpen}
            onClose={() => setSheetOpen(false)}
            tree={filteredTree}
            router={router}
            id={id}
          />
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 0.75,
            minHeight: BHYVE_SUBNAV_MIN_HEIGHT_PX,
            minWidth: 0,
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: 999,
              bgcolor: 'rgba(23, 23, 23, 0.18)'
            }
          }}
        >
          {filteredTree.map((item, index) =>
            isNavGroup(item) ? (
              <SecondaryNavGroupPill key={`grp-${item.title}-${index}`} group={item} id={id} router={router} />
            ) : (
              <SecondaryNavLinkPill
                key={`lnk-${(item as NavLink).title}-${index}`}
                link={item as NavLink}
                id={id}
                router={router}
              />
            )
          )}
        </Box>
      )}
    </Box>
  )
}

const SecondaryNavSectionsSheet: React.FC<{
  open: boolean
  onClose: () => void
  tree: VerticalNavItemsType
  router: NextRouter
  id: string | string[] | undefined
}> = ({ open, onClose, tree, router, id }) => (
  <Drawer
    anchor='top'
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        maxHeight: 'min(88dvh, 560px)',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: T.dropdownPaperBg,
        pt: 'env(safe-area-inset-top, 0px)'
      }
    }}
  >
    <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.secondaryBarBorder}`, flexShrink: 0 }}>
      <Typography variant='subtitle1' fontWeight={700} sx={{ color: T.sidebarText }}>
        Sections
      </Typography>
    </Box>
    <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0, px: 0.5, pb: 2 }}>
      {tree.map((item, index) =>
        isNavGroup(item) ? (
          <Accordion
            key={`sheet-grp-${item.title}-${index}`}
            disableGutters
            elevation={0}
            defaultExpanded={isNavGroupSubtreeActive(router, item, id)}
            sx={{
              '&:before': { display: 'none' },
              borderBottom: `1px solid ${T.secondaryBarBorder}`,
              bgcolor: 'transparent'
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: T.sidebarText }} />}>
              <Typography fontWeight={600} fontSize={15} sx={{ color: T.sidebarText }}>
                {item.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 1, px: 1 }}>
              <List dense disablePadding>
                <DrawerNavBranch
                  router={router}
                  id={id}
                  nodes={(item.children || []) as VerticalNavItemsType}
                  onNavigate={onClose}
                />
              </List>
            </AccordionDetails>
          </Accordion>
        ) : (
          <SheetTopLevelLink
            key={`sheet-lnk-${(item as NavLink).title}-${index}`}
            link={item as NavLink}
            id={id}
            router={router}
            onNavigate={onClose}
          />
        )
      )}
    </Box>
  </Drawer>
)

const SheetTopLevelLink: React.FC<{
  link: NavLink
  id: string | string[] | undefined
  router: NextRouter
  onNavigate: () => void
}> = ({ link, id, router, onNavigate }) => {
  const href = resolveNavPath(link.path, id)
  const active = Boolean(href && isPrimaryNavLinkActive(router, href))

  return (
    <ListItemButton
      component={Link}
      href={href}
      disabled={link.disabled}
      onClick={onNavigate}
      selected={active}
      {...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      sx={{
        ...subNavDropdownItemSx(active),
        borderRadius: 1,
        mx: 0.5,
        my: 0.25
      }}
    >
      {link.title}
    </ListItemButton>
  )
}

const DrawerNavBranch: React.FC<{
  router: NextRouter
  id: string | string[] | undefined
  nodes: VerticalNavItemsType
  onNavigate: () => void
}> = ({ router, id, nodes, onNavigate }) => (
  <>
    {nodes.map((node, i) => {
      if (isNavSectionTitle(node)) return null

      if (isNavGroup(node)) {
        return (
          <React.Fragment key={`${node.title}-sub-${i}`}>
            <ListSubheader
              sx={{
                typography: 'caption',
                fontWeight: 700,
                color: T.sidebarTextMuted,
                lineHeight: '32px',
                bgcolor: 'transparent'
              }}
            >
              {node.title}
            </ListSubheader>
            <DrawerNavBranch
              router={router}
              id={id}
              nodes={(node.children || []) as VerticalNavItemsType}
              onNavigate={onNavigate}
            />
          </React.Fragment>
        )
      }

      const link = node as NavLink
      const href = resolveNavPath(link.path, id)
      const active = Boolean(href && isPrimaryNavLinkActive(router, href))

      return (
        <ListItemButton
          key={`${link.title}-${href}-${i}`}
          component={Link}
          href={href}
          disabled={link.disabled}
          onClick={onNavigate}
          selected={active}
          {...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          sx={subNavDropdownItemSx(active)}
        >
          {link.title}
        </ListItemButton>
      )
    })}
  </>
)

const SecondaryNavLinkPill: React.FC<{
  link: NavLink
  id: string | string[] | undefined
  router: ReturnType<typeof useRouter>
}> = ({ link, id, router }) => {
  const href = resolveNavPath(link.path, id)
  const active = href ? isPrimaryNavLinkActive(router, href) : false

  return (
    <Button
      component={Link}
      href={href}
      disabled={link.disabled}
      {...(link.openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      sx={pillSx(active)}
    >
      {link.title}
    </Button>
  )
}

const SecondaryNavGroupPill: React.FC<{
  group: NavGroup
  id: string | string[] | undefined
  router: ReturnType<typeof useRouter>
}> = ({ group, id, router }) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null)
  const open = Boolean(anchor)
  const active = isNavGroupSubtreeActive(router, group, id)
  const childTree = (group.children || []) as VerticalNavItemsType

  useEffect(() => {
    const close = () => setAnchor(null)
    router.events.on('routeChangeComplete', close)

    return () => {
      router.events.off('routeChangeComplete', close)
    }
  }, [router.events])

  return (
    <>
      <Button
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={e => setAnchor(e.currentTarget)}
        endIcon={<Icon icon='mdi:chevron-down' width={18} height={18} />}
        sx={pillSx(active)}
      >
        {group.title}
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 220,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(23, 23, 23, 0.12)',
            bgcolor: T.dropdownPaperBg
          }
        }}
      >
        <SubNavMenuBranch router={router} id={id} nodes={childTree} onNavigate={() => setAnchor(null)} />
      </Menu>
    </>
  )
}

const SubNavMenuBranch: React.FC<{
  router: ReturnType<typeof useRouter>
  id: string | string[] | undefined
  nodes: VerticalNavItemsType
  onNavigate: () => void
}> = ({ router, id, nodes, onNavigate }) => {
  return (
    <>
      {nodes.map((node, i) => {
        if (isNavSectionTitle(node)) return null

        if (isNavGroup(node)) {
          return (
            <Box key={`${node.title}-${i}`}>
              <ListSubheader
                sx={{
                  typography: 'caption',
                  fontWeight: 700,
                  color: T.sidebarTextMuted,
                  lineHeight: '32px'
                }}
              >
                {node.title}
              </ListSubheader>
              <SubNavMenuBranch
                router={router}
                id={id}
                nodes={(node.children || []) as VerticalNavItemsType}
                onNavigate={onNavigate}
              />
            </Box>
          )
        }

        const link = node as NavLink
        const href = resolveNavPath(link.path, id)
        const active = Boolean(href && isPrimaryNavLinkActive(router, href))

        return (
          <MenuItem
            key={`${link.title}-${href}-${i}`}
            component={Link}
            href={href}
            onClick={onNavigate}
            aria-current={active ? 'page' : undefined}
            sx={subNavDropdownItemSx(active)}
          >
            {link.title}
          </MenuItem>
        )
      })}
    </>
  )
}
